import React from 'react';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';

// dragHandlers is optional. When provided it should be:
// { draggedTag, dragOverTag, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd }
const TagCloud = ({ tags, onNavigate, dragHandlers }) => {
  if (!tags || tags.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center text-gray-500 dark:text-gray-400">
        No tags to display.
      </div>
    );
  }

  const counts = tags.map(t => t.count);
  const minCount = Math.min(...counts);
  const maxCount = Math.max(...counts);
  const range = maxCount - minCount || 1;

  const getFontSize = (count) => {
    const ratio = (count - minCount) / range;
    return 0.75 + ratio * 1.25; // 0.75rem to 2rem
  };

  const getOpacity = (count) => {
    const ratio = (count - minCount) / range;
    return 0.65 + ratio * 0.35;
  };

  const sorted = [...tags].sort((a, b) => b.count - a.count);

  const dnd = dragHandlers;
  const isDragging = !!dnd?.draggedTag;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
      {isDragging && (
        <p className="text-xs text-center text-gray-400 dark:text-gray-500 animate-pulse mb-4">
          Drop <span className="font-medium text-gray-600 dark:text-gray-300">"{dnd.draggedTag.tagName}"</span> onto another tag to merge it
        </p>
      )}
      <div className="flex flex-wrap gap-3 justify-center">
        {sorted.map(tag => {
          const isBeingDragged = dnd?.draggedTag?.tagName === tag.tagName;
          const isDropTarget = dnd?.dragOverTag?.tagName === tag.tagName;

          return (
            <button
              key={tag.tagName}
              draggable={!!dnd}
              onDragStart={dnd ? e => dnd.onDragStart(e, tag) : undefined}
              onDragOver={dnd ? e => dnd.onDragOver(e, tag) : undefined}
              onDragLeave={dnd ? dnd.onDragLeave : undefined}
              onDrop={dnd ? e => dnd.onDrop(e, tag) : undefined}
              onDragEnd={dnd ? dnd.onDragEnd : undefined}
              onClick={() => !isDragging && onNavigate(tag.tagName)}
              title={isDropTarget
                ? `Merge "${dnd.draggedTag.tagName}" into "${tag.tagName}"`
                : `${tag.count} bookmark${tag.count !== 1 ? 's' : ''}`
              }
              style={
                isDropTarget || isBeingDragged ? undefined : {
                  fontSize: `${getFontSize(tag.count)}rem`,
                  opacity: getOpacity(tag.count),
                }
              }
              className={`px-3 py-1 rounded-full font-medium transition-all ${
                isDropTarget
                  ? 'bg-indigo-200 dark:bg-indigo-800 text-indigo-900 dark:text-indigo-200 ring-2 ring-indigo-400 scale-110 text-base'
                  : isBeingDragged
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 opacity-40 text-base cursor-grabbing'
                  : isDragging
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 hover:text-indigo-800 dark:hover:text-indigo-300 cursor-copy text-base'
                  : 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 hover:text-indigo-800 dark:hover:text-indigo-300 cursor-pointer'
              }`}
            >
              <span className="flex items-center gap-1">
                {isDropTarget && <ArrowsRightLeftIcon className="h-3.5 w-3.5 flex-shrink-0" />}
                {tag.tagName}
              </span>
            </button>
          );
        })}
      </div>
      {dnd && !isDragging && tags.length > 1 && (
        <p className="text-xs text-center text-gray-400 dark:text-gray-500 mt-4">
          Drag a tag onto another to merge them
        </p>
      )}
    </div>
  );
};

export default TagCloud;
